import { defineStore, storeToRefs } from 'pinia';
import {
    computed,
    nextTick,
    onScopeDispose,
    ref,
    watch,
} from 'vue';
import type { Ticket } from '@/interface/ITicket';
import {
    BetSlipCtaAction,
    BetSlipState,
    BetSlipType,
    PriceChangeStatus,
    SingleModeBetSlipTab,
} from '@/interface/enum';
import type { IVoucher } from '@/interface/voucher';
import { createKeyedTimers } from '@/libraries/betSlip/keyedTimers';
import { getLastStake } from '@/libraries/betSlip/lastStakeStorage';
import { buildVoucherTicketError } from '@/libraries/betSlip/singleModeBetSlipErrorMessage';
import { exceedsStakeMaxLength } from '@/libraries/betSlip/stake';
import {
    assertNever,
    getAffordableTicketStake,
    getTicketMaxPayout,
    hasPlaceableStake,
    hasStakeAndUnacceptedOddsChange,
    isVoucherCapable,
    validateTicketStake,
} from '@/libraries/betSlip/ticketHelper';
import { createTrailingRefreshQueue } from '@/libraries/betSlip/trailingRefreshQueue';
import { useParlayInSingle } from '@/libraries/composables/useParlayInSingle';
import { useSingleModeBalance } from '@/libraries/composables/useSingleModeBalance';
import { useSingleModeOpenTicket } from '@/libraries/composables/useSingleModeOpenTicket';
import { useSingleModePlaceTicket } from '@/libraries/composables/useSingleModePlaceTicket';
import { useSingleModeVoucher } from '@/libraries/composables/useSingleModeVoucher';
import { EMPTY_STAKE } from '@/libraries/composables/useStakeInput';
import { getStakeInputMultiplier } from '@/libraries/helpers/stakeInputHelper';
import { i18n } from '@/libraries/i18n';
import { formatMinDecimalPlace } from '@/libraries/utils/text';
import { useAccountStore } from '@/stores/accountStore';
import { useBetBuilderBetSlipStore } from '@/stores/betSlip/betBuilderBetSlipStore';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

export const TICKET_REMOVAL_DELAY_MS = 3000;

export const SUBSCRIPTION_REFRESH_DEBOUNCE_MS = 100;

const TREBLE_SUB_BET_COUNT = 3;

export interface SingleModeSnapshot {
    tickets: Record<string, Ticket>;
    isRedeemVoucher: boolean;
    activeTab: SingleModeBetSlipTab;
    voucherSelections?: Record<string, string>;
    parlayStake?: number;
}

export interface IResolveSingleTabCtaActionInput {
    isBalanceBelowEnteredTicketMinimum: boolean;
    shouldShowDeposit: boolean;
    shouldUpdateStakeForBalance: boolean;
    hasMultipleStakeBalanceBlock: boolean;
    hasInvalidStake: boolean;
    hasUnacceptedOddsChange: boolean;
    isAcceptAnyOddsEnabled: boolean;
}

export function resolveSingleTabCtaAction(
    input: IResolveSingleTabCtaActionInput,
): BetSlipCtaAction {
    if (input.hasMultipleStakeBalanceBlock) {
        return BetSlipCtaAction.PlaceBet;
    }
    if (input.isBalanceBelowEnteredTicketMinimum) {
        return input.shouldShowDeposit
            ? BetSlipCtaAction.Deposit
            : BetSlipCtaAction.PlaceBet;
    }
    if (input.shouldUpdateStakeForBalance || input.hasInvalidStake) {
        return BetSlipCtaAction.UpdateStake;
    }
    if (!input.isAcceptAnyOddsEnabled && input.hasUnacceptedOddsChange) {
        return BetSlipCtaAction.AcceptChange;
    }
    return BetSlipCtaAction.PlaceBet;
}

export interface IResolveSingleTabCtaTicketCountInput {
    ctaAction: BetSlipCtaAction;
    enteredStakeTicketCount: number;
    updatingStakeTicketCount: number;
    unacceptedOddsChangeTicketCount: number;
}

export function resolveSingleTabCtaTicketCount(
    input: IResolveSingleTabCtaTicketCountInput,
): number {
    switch (input.ctaAction) {
        case BetSlipCtaAction.UpdateStake:
            return input.updatingStakeTicketCount;
        case BetSlipCtaAction.AcceptChange:
            return input.unacceptedOddsChangeTicketCount;
        default:
            return input.enteredStakeTicketCount;
    }
}

export const useSingleModeBetSlipStore = defineStore('singleModeBetSlip', () => {
    const tickets = ref<Record<string, Ticket>>({});
    const activeTab = ref(SingleModeBetSlipTab.Single);
    const activeStakeTicketKey = ref<string | null>(null);
    const isStakePadOpen = computed(() => activeStakeTicketKey.value !== null);
    const isRestoringTickets = ref(false);
    let latestRestoreRequestId = 0;
    const removalTimers = createKeyedTimers();
    const openTicketErrorTimers = createKeyedTimers();
    const { customerInfo, balance } = storeToRefs(useAccountStore());
    const { formatMoneyWithSymbolAndDecimal, setBalance } = useAccountStore();
    const stakeInputMultiplier = computed(() => getStakeInputMultiplier(customerInfo.value.currency));
    const betSlipStore = useBetSlipStore();
    const {
        betSlipState,
        betSlipType,
        lastBetSlipType,
        isShowSingleModeBetSlip: isSingleModeBetSlipOpen,
    } = storeToRefs(betSlipStore);
    const {
        closeBetSlip,
        setBetSlipType,
        setBetSlipState,
        setLastBetSlipType,
        setLastBetSlipState,
    } = useBetSlipStore();
    const {
        isShowDepositInTicketEnabled,
        isSingleModeBetSlipEnabled,
        isVoucherEnabled,
    } = storeToRefs(useFeatureToggleStore());
    const multipleSingleTickets = computed(() => Object.values(tickets.value));
    const hasTickets = computed(() => multipleSingleTickets.value.length > 0);
    const isSingleModeTicketListVisible = computed(() => (
        isSingleModeBetSlipOpen.value
        && (
            betSlipState.value === BetSlipState.Medium
            || betSlipState.value === BetSlipState.Big
        )
    ));
    const isSingleTicketListActive = computed(() => (
        isSingleModeTicketListVisible.value
        && activeTab.value === SingleModeBetSlipTab.Single
    ));

    const {
        isRedemptionEnabled: isVoucherRedemptionEnabled,
        hasAvailableVoucher,
        voucherSelections,
        applyTicketVouchers,
        getTicketVouchers,
        getTicketVoucherOptions,
        getSelectedVoucherId,
        getSelectedVoucher,
        getSelectedVoucherStake,
        isTicketRedeemable,
        selectVoucher,
        clearVoucher,
        setRedemptionEnabled,
        restoreVoucherState,
        removeTicketVouchers,
        removeAllTicketVouchers,
    } = useSingleModeVoucher(multipleSingleTickets);

    const isShowVoucherSwitch = computed(() => isVoucherEnabled.value && hasAvailableVoucher.value);

    const invalidStakeTickets = computed(() => multipleSingleTickets.value
        .filter(ticket => !ticket.isPendingRemoval && ticket.stakeError !== null));
    const unacceptedOddsChangeTickets = computed(() => multipleSingleTickets.value
        .filter(ticket => !ticket.isPendingRemoval && hasStakeAndUnacceptedOddsChange(ticket)));

    function isTicketPlaceable(ticket: Ticket): boolean {
        if (ticket.isPendingRemoval || ticket.openTicketError !== null) {
            return false;
        }
        return isVoucherRedemptionEnabled.value
            ? isTicketRedeemable(ticket)
            : hasPlaceableStake(ticket);
    }

    const placeableTickets = computed(() => multipleSingleTickets.value.filter(isTicketPlaceable));

    function getEffectiveTicketStake(ticket: Ticket): number {
        return isVoucherRedemptionEnabled.value && isVoucherCapable(ticket)
            ? getSelectedVoucherStake(ticket) ?? 0
            : ticket.stake;
    }
    const {
        ticketsWithEnteredStake,
        isBalanceBelowEnteredTicketMinimum,
        isSingleStakeBalanceExceeded,
        isMultipleStakeBalanceExceeded,
        isSingleStakeBalanceAdjustable,
        shouldShowDeposit,
        balanceError,
    } = useSingleModeBalance({
        tickets: multipleSingleTickets,
        isActive: computed(() => activeTab.value === SingleModeBetSlipTab.Single),
        isVoucherRedemptionEnabled,
        balance,
        isShowDepositInTicketEnabled,
        playerType: computed(() => customerInfo.value.playerType),
    });

    const {
        openTicket: openTicketInternal,
        refreshTicket: refreshTicketInternal,
        refreshAllTickets: refreshAllTicketsInternal,
        openAllTicketsForSingleTab,
        cancelOpenTicket,
        cancelAllOpenTickets,
        markTicketClosed: markTicketClosedInternal,
        showTemporaryOpenTicketError,
    } = useSingleModeOpenTicket({
        tickets: multipleSingleTickets,
        getTicket,
        removalTimers,
        openTicketErrorTimers,
        openTicketErrorDelayMs: TICKET_REMOVAL_DELAY_MS,
        lockAndScheduleRemoval,
        applyVoucherResult: applyTicketVoucherResult,
        setBalance,
    });
    // While the list was hidden its Ticket subscriptions were unmounted, so the
    // prices carry an unbounded stale window — unlike a subscription-driven
    // background refresh, this catch-up blocks placing until it lands.
    const isRefreshingRevealedTickets = ref(false);
    watch(
        [multipleSingleTickets, isSingleTicketListActive],
        async ([, isActive], [previousTickets, wasActive]) => {
            const hadTicketsBeforeActivation = previousTickets.length > 0;
            if (!isActive || wasActive || !hadTicketsBeforeActivation) {
                return;
            }
            isRefreshingRevealedTickets.value = true;
            try {
                await openAllTicketsForSingleTab();
            } finally {
                isRefreshingRevealedTickets.value = false;
                resetRedemptionWithoutAvailableVoucher();
            }
        },
        { flush: 'sync' },
    );
    const summaryTickets = computed(() => (
        isVoucherRedemptionEnabled.value
            ? placeableTickets.value
            : ticketsWithEnteredStake.value
    ));
    const totalBetCount = computed(() => summaryTickets.value.length);
    const totalStake = computed(() => summaryTickets.value
        .reduce((total, ticket) => total + getEffectiveTicketStake(ticket), 0));
    const totalMaxPayout = computed(() => summaryTickets.value
        .reduce((total, ticket) => total + getTicketMaxPayoutValue(ticket), 0));
    const isPlacingBet = computed(() => multipleSingleTickets.value
        .some(ticket => ticket.isPlacingBet));
    // A background refresh (live odds pushes re-open every few seconds) keeps the
    // previous uid, so it must not block placing; a never-opened Ticket has no uid
    // yet and does.
    const hasOpeningNewTickets = computed(() => multipleSingleTickets.value
        .some(ticket => ticket.isOpeningTicket && ticket.uid === ''));
    const {
        placeBetFooterError,
        placeSingleTabTickets,
        showPlaceBetFooterError,
        clearPlaceBetFooterError,
    } = useSingleModePlaceTicket({
        placeableTickets,
        allTickets: multipleSingleTickets,
        hasTickets,
        isPlacingBet,
        getTicket,
        removeTicket: removeTicketInternal,
        removeAllTickets: removeAllTicketsInternal,
        updateTicketStake,
        refreshTicket: refreshTicketInternal,
        refreshAllTickets: refreshAllTicketsInternal,
        closeStakePad,
        getEffectiveTicketStake,
        getOrderVoucherId,
        isVoucherRedemptionEnabled,
        placeBetFooterErrorDelayMs: TICKET_REMOVAL_DELAY_MS,
    });

    const {
        orderedParlayTickets: parlayInSingleTickets,
        parlayTicketGroups,
        conflictedParlayKeys,
        parlaySubBets,
        parlayOddsByKey,
        minParlaySubBetCount,
        missingParlaySubBetCount,
        isParlaySubBetCountValid,
        isValid2MpSubBets,
        hasIneligibleParlayTickets,
        hasConflictedParlayTickets,
        hasBlockedParlaySelections,
        shouldShowParlayInsufficientMessage,
        parlayMinBet,
        parlayMaxBet,
        isOpeningParlay,
        isParlayOpenBlocked,
        parlayOpenTicketError,
        parlayOpenSubBetResults,
        parlayDisplaySubBetResults,
        parlaySubBetErrors,
        parlaySubBetChangeErrors,
        parlayTotalOdds,
        parlayDisplayOdds,
        parlayStake,
        parlayStakeError,
        isShowCombo,
        comboParlayOptions,
        parlayTotalBetCount,
        parlayTotalStake,
        isParlayBalanceBelowMinimum,
        isParlayRequiredBalanceExceeded,
        isParlayStakeBalanceAdjustable,
        shouldShowParlayDeposit,
        parlayBalanceError,
        parlayMaxPayout,
        isParlayOddsChangeAccepted,
        isParlayStakePadOpen,
        isPlacingParlay,
        parlayCtaAction,
        isParlayCtaDisabled,
        setParlayStake: setParlayStakeValue,
        appendParlayStakeDigits: appendParlayStakeDigitsValue,
        addParlayStakeAmount: addParlayStakeAmountValue,
        setMaxParlayStake: setMaxParlayStakeValue,
        backspaceParlayStake: backspaceParlayStakeValue,
        updateParlayStakeForLimits: updateParlayStakeForLimitsValue,
        resetParlayStake: resetParlayStakeValue,
        openParlayStakePad: openParlayStakePadValue,
        updateParlayOdds,
        closeParlayStakePad,
        acceptParlayOddsChanges: acceptParlayOddsChangesValue,
        openParlayTicket: openParlayTicketValue,
        addOrRemoveSelectedCombo: addOrRemoveSelectedComboValue,
        handleParlayCtaClick,
    } = useParlayInSingle({
        tickets: multipleSingleTickets,
        activeTab,
        isPlacingBet,
        isTicketListVisible: isSingleModeTicketListVisible,
        removeAllTickets: removeAllTicketsInternal,
        markTicketClosed: markTicketClosedInternal,
    });
    const isBetSlipInteractionLocked = computed(() => (
        isPlacingBet.value || isPlacingParlay.value
    ));

    function lockBetSlipInteraction<TArguments extends unknown[], TResult>(
        action: (...args: TArguments) => TResult,
    ) {
        return (...args: TArguments): TResult | undefined => (
            isBetSlipInteractionLocked.value ? undefined : action(...args)
        );
    }
    const setParlayStake = lockBetSlipInteraction(setParlayStakeValue);
    const appendParlayStakeDigits = lockBetSlipInteraction(appendParlayStakeDigitsValue);
    const addParlayStakeAmount = lockBetSlipInteraction(addParlayStakeAmountValue);
    const setMaxParlayStake = lockBetSlipInteraction(setMaxParlayStakeValue);
    const backspaceParlayStake = lockBetSlipInteraction(backspaceParlayStakeValue);
    const updateParlayStakeForLimits = lockBetSlipInteraction(updateParlayStakeForLimitsValue);
    const resetParlayStake = lockBetSlipInteraction(resetParlayStakeValue);
    const openParlayStakePad = lockBetSlipInteraction(openParlayStakePadValue);
    const acceptParlayOddsChanges = lockBetSlipInteraction(acceptParlayOddsChangesValue);
    const addOrRemoveSelectedCombo = lockBetSlipInteraction(addOrRemoveSelectedComboValue);
    const refreshParlayOpenTicket = lockBetSlipInteraction(() => {
        // Several SubBet subscriptions can update together. Do not let a
        // background refresh cancel the active Open, especially an odds accept.
        if (isOpeningParlay.value) {
            return undefined;
        }
        return openParlayTicketValue();
    });
    const removeTicket = lockBetSlipInteraction(removeTicketInternal);
    const removeAllTickets = lockBetSlipInteraction(removeAllTicketsInternal);
    const setVoucher = lockBetSlipInteraction(setVoucherInternal);
    const refreshTicket = lockBetSlipInteraction((key: string) => {
        // A Ticket subscription can update while initial/accept Open owns the request.
        // Keep that request from being cancelled by a background refresh.
        if (getTicket(key)?.isOpeningTicket) {
            return undefined;
        }
        return refreshTicketInternal(key);
    });
    const ticketSubscriptionRefreshQueue = createTrailingRefreshQueue<string>({
        delayMs: SUBSCRIPTION_REFRESH_DEBOUNCE_MS,
        isBlocked: key => (
            isBetSlipInteractionLocked.value
            || getTicket(key)?.isOpeningTicket === true
        ),
        refresh: refreshTicketInternal,
    });
    const parlaySubscriptionRefreshQueue = createTrailingRefreshQueue<'parlay'>({
        delayMs: SUBSCRIPTION_REFRESH_DEBOUNCE_MS,
        isBlocked: () => isBetSlipInteractionLocked.value || isOpeningParlay.value,
        refresh: async () => {
            await openParlayTicketValue();
        },
    });
    const queueTicketRefresh = lockBetSlipInteraction((key: string) => (
        ticketSubscriptionRefreshQueue.queue(key)
    ));
    const queueParlayOpenTicketRefresh = lockBetSlipInteraction(() => (
        parlaySubscriptionRefreshQueue.queue('parlay')
    ));
    watch(
        () => multipleSingleTickets.value
            .map(ticket => `${ticket.key}:${ticket.isOpeningTicket}`)
            .join('|'),
        () => ticketSubscriptionRefreshQueue.resumeAll(),
        { flush: 'sync' },
    );
    watch(isOpeningParlay, () => parlaySubscriptionRefreshQueue.resumeAll(), { flush: 'sync' });
    watch(isBetSlipInteractionLocked, (isLocked) => {
        if (isLocked) {
            return;
        }
        ticketSubscriptionRefreshQueue.resumeAll();
        parlaySubscriptionRefreshQueue.resumeAll();
    }, { flush: 'sync' });
    const refreshAllTickets = lockBetSlipInteraction(refreshAllTicketsInternal);
    const markTicketClosed = lockBetSlipInteraction(markTicketClosedInternal);
    const ctaAction = computed(() => resolveSingleTabCtaAction({
        isBalanceBelowEnteredTicketMinimum: isBalanceBelowEnteredTicketMinimum.value,
        shouldShowDeposit: shouldShowDeposit.value,
        shouldUpdateStakeForBalance: isSingleStakeBalanceAdjustable.value,
        hasMultipleStakeBalanceBlock: isMultipleStakeBalanceExceeded.value,
        hasInvalidStake: invalidStakeTickets.value.length > 0,
        hasUnacceptedOddsChange: unacceptedOddsChangeTickets.value.length > 0,
        isAcceptAnyOddsEnabled: customerInfo.value.isAcceptAnyOdds,
    }));
    const updatingStakeTicketCount = computed(() => (
        isSingleStakeBalanceAdjustable.value
            ? ticketsWithEnteredStake.value.length
            : invalidStakeTickets.value.length
    ));
    const ctaTicketCount = computed(() => resolveSingleTabCtaTicketCount({
        ctaAction: ctaAction.value,
        enteredStakeTicketCount: totalBetCount.value,
        updatingStakeTicketCount: updatingStakeTicketCount.value,
        unacceptedOddsChangeTicketCount: unacceptedOddsChangeTickets.value.length,
    }));
    const parlayCtaTicketCount = computed(() => parlayInSingleTickets.value.length);
    const isCtaDisabled = computed(() => {
        if (isRestoringTickets.value) return true;
        if (isBetSlipInteractionLocked.value) return true;
        if (hasOpeningNewTickets.value || isRefreshingRevealedTickets.value) return true;
        if (ctaAction.value === BetSlipCtaAction.Deposit) return false;
        if (isBalanceBelowEnteredTicketMinimum.value) return true;
        if (isMultipleStakeBalanceExceeded.value) return true;
        if (isSingleStakeBalanceExceeded.value && !isSingleStakeBalanceAdjustable.value) return true;
        return ctaAction.value === BetSlipCtaAction.PlaceBet
            && placeableTickets.value.length === 0;
    });
    let isAcceptingOddsChange = false;

    const totalStakeDisplay = computed(() => formatMoneyWithSymbolAndDecimal(totalStake.value));
    const totalMaxPayoutDisplay = computed(() => formatMoneyWithSymbolAndDecimal(totalMaxPayout.value));
    const parlayMaxPayoutDisplay = computed(() => formatMoneyWithSymbolAndDecimal(parlayMaxPayout.value));
    const isParlayTabActive = computed(() => activeTab.value === SingleModeBetSlipTab.Parlay);
    const shouldShowParlaySelectionWarning = computed(() => isParlayTabActive.value
        && hasBlockedParlaySelections.value);
    const headerTicketCount = computed(() => (
        isParlayTabActive.value
            ? parlaySubBets.value.length
            : multipleSingleTickets.value.length
    ));
    const headerOddsDisplay = computed(() => {
        if (!isParlayTabActive.value) {
            return '';
        }
        return hasBlockedParlaySelections.value || parlayDisplayOdds.value === 0
            ? '--'
            : formatMinDecimalPlace(parlayDisplayOdds.value, 2);
    });
    const headerMaxPayoutDisplay = computed(() => (
        isParlayTabActive.value
            ? parlayMaxPayoutDisplay.value
            : totalMaxPayoutDisplay.value
    ));
    const parlayTitle = computed(() => {
        if (!isParlaySubBetCountValid.value || hasBlockedParlaySelections.value) {
            return i18n.t('bottom_function_bet_slip');
        }
        const subBetCount = parlaySubBets.value.length;
        return subBetCount === TREBLE_SUB_BET_COUNT
            ? i18n.t('betSlip_comboParlay_option_3')
            : i18n.t('betSlip_comboParlay_option_n', { n: subBetCount });
    });
    const betSlipTitle = computed(() => (
        !isParlayTabActive.value
            ? i18n.t('bet_slip_header_name')
            : parlayTitle.value
    ));

    async function addTicket(ticket: Ticket, onTicketRendered?: () => void) {
        if (isBetSlipInteractionLocked.value || tickets.value[ticket.key]) {
            return;
        }
        clearPlaceBetFooterError();
        openSingleModeBetSlip();
        applyRememberedStakeOnAdd(ticket);
        tickets.value = {
            ...tickets.value,
            [ticket.key]: ticket,
        };
        await nextTick();
        window.eventEmitter.emit('singleModeBetSlip:newTicketAdded');
        onTicketRendered?.();
        if (activeTab.value === SingleModeBetSlipTab.Single) {
            await openTicketInternal(ticket.key, true);
        }
    }

    async function addOrRemoveTicket(ticket: Ticket, onTicketRendered?: () => void) {
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        await useBetBuilderBetSlipStore().resetBetBuilderForSingleMode();
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        if (isTicketExist(ticket.key)) {
            openSingleModeBetSlip();
            removeTicketInternal(ticket.key);
            return;
        }
        await addTicket(ticket, onTicketRendered);
    }

    async function addOrReopenTicket(ticket: Ticket) {
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        await useBetBuilderBetSlipStore().resetBetBuilderForSingleMode();
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        if (isTicketExist(ticket.key)) {
            openSingleModeBetSlip();
            return;
        }
        await addTicket(ticket);
    }

    function openSingleModeBetSlip() {
        if (isSingleModeBetSlipOpen.value) {
            return;
        }
        setBetSlipType(BetSlipType.SingleMode);
        setBetSlipState(BetSlipState.Medium);
    }

    function removeTicketInternal(key: string) {
        if (!tickets.value[key]) {
            return;
        }
        cancelOpenTicket(key);
        removalTimers.clear(key);
        openTicketErrorTimers.clear(key);
        tickets.value = Object.fromEntries(
            Object.entries(tickets.value).filter(([ticketKey]) => ticketKey !== key),
        );
        removeTicketVouchers(key);
        resetRedemptionWithoutAvailableVoucher();
        resetStakePadAfterRemoval(key);
        closeSingleModeRouteIfEmpty();
    }

    function removeAllTicketsInternal() {
        cancelAllOpenTickets('Single mode Tickets removed');
        removalTimers.clearAll();
        openTicketErrorTimers.clearAll();
        tickets.value = {};
        removeAllTicketVouchers();
        clearPlaceBetFooterError();
        resetParlayStakeValue();
        closeStakePad();
        closeSingleModeRouteIfEmpty();
    }

    function resetSingleModeForBetBuilder() {
        if (isBetSlipInteractionLocked.value || !hasTickets.value) {
            return;
        }
        removeAllTicketsInternal();
        switchTab(SingleModeBetSlipTab.Single);
        setRedemptionEnabled(false);
    }

    function switchTab(tab: SingleModeBetSlipTab) {
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        activeTab.value = tab;
    }

    function openStakePad(key: string) {
        const ticket = getTicket(key);
        if (isBetSlipInteractionLocked.value || !ticket || ticket.isPendingRemoval) {
            return;
        }
        activeStakeTicketKey.value = key;
    }

    function closeStakePad() {
        activeStakeTicketKey.value = null;
    }

    function setStake(key: string, stake: number) {
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        const ticket = getTicket(key);
        if (!ticket || ticket.isPendingRemoval) {
            return;
        }
        if (exceedsStakeMaxLength(stake)) {
            return;
        }
        updateTicketStake(ticket, stake);
    }

    function setStakePerBet(stake: number) {
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        multipleSingleTickets.value
            .filter(ticket => !ticket.isPendingRemoval)
            .forEach(ticket => updateTicketStake(ticket, stake));
    }

    function appendStakeDigits(key: string, digits: string) {
        const ticket = getTicket(key);
        if (!ticket) {
            return;
        }
        const digitPlaceMultiplier = 10 ** digits.length;
        const nextStake = ticket.stake * digitPlaceMultiplier
            + Number(digits) * stakeInputMultiplier.value;
        setStake(key, nextStake);
    }

    function addStakeAmount(key: string, amount: number) {
        const ticket = getTicket(key);
        if (!ticket) {
            return;
        }
        setStake(key, ticket.stake + amount);
    }

    function setMaxStake(key: string) {
        const ticket = getTicket(key);
        if (!ticket) {
            return;
        }
        const maxStake = balance.value !== null
            ? getAffordableTicketStake(ticket, balance.value)
            : ticket.maxBet;
        setStake(key, maxStake);
    }

    function backspaceStake(key: string) {
        const ticket = getTicket(key);
        if (!ticket) {
            return;
        }
        const nextStake = Math.floor(ticket.stake / (10 * stakeInputMultiplier.value))
            * stakeInputMultiplier.value;
        setStake(key, nextStake);
    }

    function getTicketMaxPayoutValue(ticket: Ticket): number {
        return getTicketMaxPayout(
            ticket,
            isVoucherRedemptionEnabled.value ? getSelectedVoucherStake(ticket) : null,
        );
    }

    function switchRedeemVoucherMode(value: boolean) {
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        setRedemptionEnabled(value);
        closeStakePad();
        multipleSingleTickets.value.forEach(ticket => updateTicketStake(ticket, 0));
    }

    function setVoucherInternal(key: string, voucherId: string | null) {
        const ticket = getTicket(key);
        if (!ticket || ticket.isPendingRemoval || !isVoucherCapable(ticket)) {
            return;
        }
        if (voucherId === null) {
            clearVoucher(key);
        } else {
            selectVoucher(ticket, voucherId);
        }
    }

    function clampInvalidStakesToBetLimits() {
        invalidStakeTickets.value.forEach((ticket) => {
            if (ticket.stake < ticket.minBet) {
                updateTicketStake(ticket, ticket.minBet);
                return;
            }
            if (ticket.stake > ticket.maxBet) {
                updateTicketStake(ticket, ticket.maxBet);
            }
        });
    }

    function updateStakes() {
        if (
            isSingleStakeBalanceAdjustable.value
            && balance.value !== null
        ) {
            const [ticket] = ticketsWithEnteredStake.value;
            updateTicketStake(
                ticket,
                getAffordableTicketStake(ticket, balance.value),
            );
            return;
        }
        clampInvalidStakesToBetLimits();
    }

    async function handleSingleCtaClick() {
        if (isCtaDisabled.value) {
            return;
        }
        switch (ctaAction.value) {
            case BetSlipCtaAction.UpdateStake:
                updateStakes();
                return;
            case BetSlipCtaAction.AcceptChange:
                await acceptOddsChanges();
                return;
            case BetSlipCtaAction.PlaceBet:
                await placeSingleTabTickets();
                return;
            case BetSlipCtaAction.Deposit:
                return;
            default:
                assertNever(ctaAction.value);
        }
    }

    async function acceptOddsChanges() {
        if (isAcceptingOddsChange) {
            return;
        }
        isAcceptingOddsChange = true;
        try {
            const ticketKeys = unacceptedOddsChangeTickets.value.map(ticket => ticket.key);
            await Promise.all(ticketKeys.map(async (key) => {
                await refreshTicketInternal(key);
                const ticket = getTicket(key);
                if (ticket?.openTicketError === null && !ticket.isPendingRemoval) {
                    ticket.isOddsChangeAccepted = true;
                }
            }));
        } finally {
            isAcceptingOddsChange = false;
        }
    }

    function getOrderVoucherId(key: string): string | null {
        return isVoucherRedemptionEnabled.value ? getSelectedVoucherId(key) : null;
    }

    function isTicketExist(key: string): boolean {
        return tickets.value[key] !== undefined;
    }

    function createSnapshot(): SingleModeSnapshot {
        return {
            tickets: tickets.value,
            isRedeemVoucher: isVoucherRedemptionEnabled.value,
            activeTab: activeTab.value,
            voucherSelections: voucherSelections.value,
            parlayStake: parlayStake.value,
        };
    }

    async function restoreTickets(snapshot: SingleModeSnapshot) {
        if (!isSingleModeBetSlipEnabled.value) {
            closeSingleModeRouteIfEmpty();
            return;
        }
        const restored = Object.values(snapshot.tickets);
        if (restored.length === 0) {
            return;
        }
        const restoreRequestId = ++latestRestoreRequestId;
        isRestoringTickets.value = true;
        try {
            restoreVoucherState({
                isRedeem: snapshot.isRedeemVoucher,
                selections: snapshot.voucherSelections ?? {},
            });
            setParlayStakeValue(snapshot.parlayStake ?? 0);
            switchTab(snapshot.activeTab);
            restored.forEach((ticket) => {
                ticket.isOpeningTicket = false;
                ticket.isPlacingBet = false;
                ticket.isPendingRemoval = false;
                ticket.openTicketError = null;
                ticket.oddsChangeStatus = PriceChangeStatus.Remain;
                ticket.isOddsChangeAccepted = true;
            });
            tickets.value = Object.fromEntries(restored.map(ticket => [ticket.key, ticket]));
            if (activeTab.value === SingleModeBetSlipTab.Single) {
                await Promise.all(restored.map(ticket => openTicketInternal(ticket.key, true)));
            }
        } finally {
            if (restoreRequestId === latestRestoreRequestId) {
                isRestoringTickets.value = false;
                resetRedemptionWithoutAvailableVoucher();
            }
        }
    }

    function lockAndScheduleRemoval(key: string) {
        const ticket = getTicket(key);
        if (!ticket) {
            return;
        }
        ticket.isPendingRemoval = true;
        if (activeStakeTicketKey.value === key) {
            closeStakePad();
        }
        openTicketErrorTimers.clear(key);
        removalTimers.schedule(key, TICKET_REMOVAL_DELAY_MS, () => removeTicketInternal(key));
    }

    function getTicket(key: string): Ticket | undefined {
        return tickets.value[key];
    }

    function applyTicketVoucherResult(ticket: Ticket, rawVouchers: IVoucher[] | null) {
        if (!isVoucherCapable(ticket)) {
            return;
        }
        applyTicketVouchers(ticket.key, rawVouchers);
        if (!isVoucherRedemptionEnabled.value) {
            return;
        }
        reconcileTicketVoucherSelection(ticket);
        resetRedemptionWithoutAvailableVoucher();
    }

    function reconcileTicketVoucherSelection(ticket: Ticket) {
        // The refreshed list no longer offers the selected Voucher: drop the selection
        // and skip the eligibility check, which has no Voucher left to inspect.
        if (getSelectedVoucherId(ticket.key) !== null && getSelectedVoucher(ticket) === null) {
            clearVoucher(ticket.key);
            return;
        }
        const voucherTicketError = buildVoucherTicketError(getSelectedVoucher(ticket));
        if (voucherTicketError === null) {
            return;
        }
        showTemporaryOpenTicketError(ticket, voucherTicketError);
        setVoucherInternal(ticket.key, null);
    }

    function resetRedemptionWithoutAvailableVoucher() {
        if (isRestoringTickets.value || isRefreshingRevealedTickets.value) {
            return;
        }
        if (isVoucherRedemptionEnabled.value && !isShowVoucherSwitch.value) {
            setRedemptionEnabled(false);
        }
    }

    function applyRememberedStakeOnAdd(ticket: Ticket) {
        if (isVoucherRedemptionEnabled.value) {
            return;
        }
        const ticketCountBeforeAdd = multipleSingleTickets.value.length;
        if (ticketCountBeforeAdd === 0) {
            const rememberedStake = getLastStake();
            ticket.stake = rememberedStake;
            setParlayStakeValue(rememberedStake);
            return;
        }
        if (ticketCountBeforeAdd === 1) {
            clearRememberedStakeOnSecondTicket();
        }
    }

    function clearRememberedStakeOnSecondTicket() {
        const [firstTicket] = multipleSingleTickets.value;
        if (firstTicket && firstTicket.stake === getLastStake()) {
            updateTicketStake(firstTicket, EMPTY_STAKE);
        }
    }

    function updateTicketStake(ticket: Ticket, stake: number) {
        ticket.stake = stake;
        ticket.stakeError = validateTicketStake(ticket);
        clearPlaceBetFooterError();
    }

    function resetStakePadAfterRemoval(removedKey: string) {
        if (activeStakeTicketKey.value === removedKey || !hasTickets.value) {
            closeStakePad();
        }
    }

    function closeSingleModeRouteIfEmpty() {
        if (hasTickets.value) {
            return;
        }
        setRedemptionEnabled(false);
        if (betSlipType.value === BetSlipType.SingleMode) {
            closeBetSlip();
        }
        if (lastBetSlipType.value === BetSlipType.SingleMode) {
            setLastBetSlipType(BetSlipType.None);
            setLastBetSlipState(BetSlipState.Closed);
        }
    }

    onScopeDispose(() => {
        cancelAllOpenTickets('Single mode Store disposed');
        ticketSubscriptionRefreshQueue.clearAll();
        parlaySubscriptionRefreshQueue.clearAll();
        removalTimers.clearAll();
        openTicketErrorTimers.clearAll();
    });

    return {
        tickets,
        activeTab,
        activeStakeTicketKey,
        isStakePadOpen,
        isVoucherRedemptionEnabled,
        isShowVoucherSwitch,
        placeBetFooterError,
        showPlaceBetFooterError,
        multipleSingleTickets,
        parlayInSingleTickets,
        parlayTicketGroups,
        conflictedParlayKeys,
        parlaySubBets,
        parlayOddsByKey,
        minParlaySubBetCount,
        missingParlaySubBetCount,
        isParlaySubBetCountValid,
        isValid2MpSubBets,
        hasIneligibleParlayTickets,
        hasConflictedParlayTickets,
        shouldShowParlayInsufficientMessage,
        parlayMinBet,
        parlayMaxBet,
        isOpeningParlay,
        isParlayOpenBlocked,
        parlayOpenTicketError,
        parlayOpenSubBetResults,
        parlayDisplaySubBetResults,
        parlaySubBetErrors,
        parlaySubBetChangeErrors,
        parlayTotalOdds,
        parlayDisplayOdds,
        parlayStake,
        parlayStakeError,
        isShowCombo,
        comboParlayOptions,
        parlayTotalBetCount,
        parlayTotalStake,
        isParlayBalanceBelowMinimum,
        isParlayRequiredBalanceExceeded,
        isParlayStakeBalanceAdjustable,
        shouldShowParlayDeposit,
        parlayBalanceError,
        parlayMaxPayout,
        isParlayOddsChangeAccepted,
        isParlayStakePadOpen,
        isPlacingParlay,
        parlayCtaAction,
        isParlayCtaDisabled,
        setParlayStake,
        appendParlayStakeDigits,
        addParlayStakeAmount,
        setMaxParlayStake,
        backspaceParlayStake,
        updateParlayStakeForLimits,
        resetParlayStake,
        openParlayStakePad,
        updateParlayOdds,
        closeParlayStakePad,
        acceptParlayOddsChanges,
        refreshParlayOpenTicket,
        queueParlayOpenTicketRefresh,
        addOrRemoveSelectedCombo,
        handleParlayCtaClick,
        hasTickets,
        invalidStakeTickets,
        unacceptedOddsChangeTickets,
        shouldShowDeposit,
        balanceError,
        totalBetCount,
        totalStake,
        totalMaxPayout,
        isPlacingBet,
        isBetSlipInteractionLocked,
        ctaAction,
        ctaTicketCount,
        parlayCtaTicketCount,
        isCtaDisabled,
        totalStakeDisplay,
        totalMaxPayoutDisplay,
        parlayMaxPayoutDisplay,
        shouldShowParlaySelectionWarning,
        headerTicketCount,
        headerOddsDisplay,
        headerMaxPayoutDisplay,
        betSlipTitle,
        getTicketVouchers,
        getTicketVoucherOptions,
        getSelectedVoucherId,
        getTicketMaxPayoutValue,
        createSnapshot,
        addTicket,
        addOrRemoveTicket,
        addOrReopenTicket,
        removeTicket,
        removeAllTickets,
        resetSingleModeForBetBuilder,
        switchTab,
        openStakePad,
        closeStakePad,
        setStake,
        setStakePerBet,
        switchRedeemVoucherMode,
        setVoucher,
        appendStakeDigits,
        addStakeAmount,
        setMaxStake,
        backspaceStake,
        handleSingleCtaClick,
        isTicketExist,
        refreshTicket,
        queueTicketRefresh,
        refreshAllTickets,
        restoreTickets,
        markTicketClosed,
    };
});
